import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { usePartnerAuth } from './usePartnerAuth';
import { buildMemorialDisplayName } from "@/utils/memorialDisplayName";

export interface OrderItem {
  id: string;
  product_id: string;
  product_name: string;
  product_category?: 'flowers' | 'cleaning' | 'repairs';
  quantity: number;
  unit_price_cents: number;
  line_total_cents: number;
  fulfillment_cost_cents: number | null;
}

export interface PartnerOrder {
  id: string;
  memorial_id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string | null;
  service_type: string;
  delivery_date: string;
  delivery_window_start: string | null;
  delivery_window_end: string | null;
  special_instructions: string | null;
  status: 'pending' | 'assigned' | 'accepted' | 'in_progress' | 'delivered' | 'cancelled';
  total_cents: number;
  total_fulfillment_cost_cents: number | null;
  partner_payout_cents: number | null;
  delivery_latitude: number | null;
  delivery_longitude: number | null;
  delivery_photo_url: string | null;
  created_at: string;
  accepted_at: string | null;
  delivered_at: string | null;
  memorial_name: string;
  memorial_profile_picture_url: string | null;
  headstone_photo_url: string | null;
  order_items: OrderItem[];
  memorial_slug: string | null;
  // Commission fields for payout records
  monument_company_id: string | null;
  commission_amount_cents: number | null;
  subscription_id: string | null;
  stripe_payment_intent_id: string | null;
  // Client-side estimated earnings (fallback when DB fields are NULL)
  estimated_earnings_cents: number | null;
}

export const usePartnerOrders = () => {
  const { partner } = usePartnerAuth();
  const queryClient = useQueryClient();

  const { data: orders, isLoading, error } = useQuery({
    queryKey: ['partner-orders', partner?.id],
    queryFn: async () => {
      if (!partner?.id) return [];

      const { data: ordersData, error: ordersError } = await supabase
        .from('orders')
        .select(`
          *,
          memorials!inner(first_name, last_name, profile_picture_url, slug)
        `)
        .eq('fulfillment_partner_id', partner.id)
        .order('delivery_date', { ascending: true });

      if (ordersError) throw ordersError;

      // Fetch order items with product category for each order
      const orderIds = ordersData.map(o => o.id);
      const { data: itemsData, error: itemsError } = await supabase
        .from('order_items')
        .select(`
          *,
          products(category)
        `)
        .in('order_id', orderIds);

      if (itemsError) throw itemsError;

      // Fetch monument installations for headstone photos
      const memorialIds = ordersData.map(o => o.memorial_id);
      const { data: installationsData } = await supabase
        .from('monument_installations')
        .select('memorial_id, photo_url')
        .in('memorial_id', memorialIds);

      const installationMap = new Map(
        installationsData?.map(i => [i.memorial_id, i.photo_url]) || []
      );

      // Fetch partner's product costs for client-side earnings estimation
      const { data: partnerCosts } = await supabase
        .from('partner_product_costs')
        .select('product_id, cost_cents')
        .eq('partner_id', partner.id);

      const costMap = new Map(
        partnerCosts?.map(c => [c.product_id, c.cost_cents]) || []
      );

      // Map orders with memorial names, items, and photos
      return ordersData.map(order => {
        const items = itemsData
          .filter(item => item.order_id === order.id)
          .map(item => ({
            ...item,
            product_category: item.products?.category
          }));

        // Calculate estimated earnings (fallback for NULL DB fields)
        let estimatedEarningsCents: number | null = null;
        if (order.partner_payout_cents == null) {
          if (partner?.partner_type === 'monument_company' && partner?.pontis_order_rate != null) {
            // ORDER-SPLIT: partner gets their percentage of GROSS (Pontis absorbs Stripe fee)
            estimatedEarningsCents = Math.round(order.total_cents * (1 - partner.pontis_order_rate));
          } else {
            // PROFIT-SPLIT: independent partner earns fulfillment cost
            const total = items.reduce((sum, item) => {
              const cost = costMap.get(item.product_id) || 0;
              return sum + cost * item.quantity;
            }, 0);
            if (total > 0) estimatedEarningsCents = total;
          }
        }

        return {
          ...order,
          memorial_name: buildMemorialDisplayName(order.memorials),
          memorial_profile_picture_url: order.memorials.profile_picture_url,
          memorial_slug: order.memorials.slug,
          headstone_photo_url: installationMap.get(order.memorial_id) || null,
          order_items: items,
          estimated_earnings_cents: estimatedEarningsCents,
        };
      }) as PartnerOrder[];
    },
    enabled: !!partner?.id,
  });

  const acceptOrder = useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase
        .from('orders')
        .update({ 
          status: 'accepted',
          accepted_at: new Date().toISOString()
        })
        .eq('id', orderId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-orders'] });
    },
  });

  const markDelivered = useMutation({
    mutationFn: async ({ orderId, file }: { orderId: string; file: File }) => {
      // Step 1: Upload photo client-side (storage allows partner uploads)
      const fileExt = file.name.split('.').pop() || 'jpg';
      const filePath = `delivery-photos/${orderId}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('public-assets')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw new Error(`Photo upload failed: ${uploadError.message}`);

      const { data: urlData } = supabase.storage
        .from('public-assets')
        .getPublicUrl(filePath);

      // Step 2: Call edge function with JSON to handle payout record,
      // order status update, and email (requires service role)
      const { data, error } = await supabase.functions.invoke('mark-order-delivered', {
        body: { orderId, photoUrl: urlData.publicUrl },
      });

      if (error) {
        // Extract the actual error message from the edge function response body
        let serverMessage = '';
        try {
          const errorBody = await error.context?.json();
          serverMessage = errorBody?.error || '';
        } catch {
          // Response body may not be JSON
        }
        console.error('mark-order-delivered error:', serverMessage || error.message);
        throw new Error(serverMessage || 'Failed to mark order as delivered');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return { photoUrl: urlData.publicUrl };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-orders'] });
    },
  });

  const scheduleDelivery = useMutation({
    mutationFn: async ({ orderId, date }: { orderId: string; date: string }) => {
      const { error } = await supabase
        .from('orders')
        .update({ delivery_date: date })
        .eq('id', orderId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partner-orders'] });
    },
  });

  return {
    orders: orders || [],
    isLoading,
    error,
    acceptOrder,
    markDelivered,
    scheduleDelivery,
  };
};
